import { useFormik } from 'formik';
import styles from './authform.module.scss';
import { useAuthentication } from '../Authentication/useAuthentication';
import { Button, InputBar, InputPassword } from '~/ui';
import { AxiosError } from 'axios';
import { useToastError } from '~/utils/useToastError';
import { validate } from '~/utils/validateAuth';
import { useTranslation } from 'react-i18next';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { useEffect } from 'react';
import { languageData } from '~/utils/language/languageData';

interface FormikValues {
  email: string;
  password: string;
}

const AuthForm = (): JSX.Element => {
  const { login } = useAuthentication();
  const toastError = useToastError();
  const { t, i18n } = useTranslation();
  const analytics = getAnalytics();
  const languages = languageData();
  const systemLanguage = window.navigator.language;
  const defaultLanguage = 'en';

  const formik = useFormik({
    initialValues: {
      password: '',
      email: '',
    },
    validate,

    onSubmit: async (values: FormikValues) => {
      try {
        await login(values.email, values.password);
        logEvent(analytics, 'login', {
          email: values.email,
          type: 'success',
        });
        formik.resetForm();
      } catch (error) {
        logEvent(analytics, 'login', {
          email: values.email,
          type: 'error',
        });
        if (!(error instanceof AxiosError)) {
          return toastError(error);
        }
        return toastError(error);
      }
    },
  });

  useEffect(() => {
    const preferLanguage = localStorage.getItem('preferLanguage');

    if (preferLanguage) {
      i18n.changeLanguage(preferLanguage);
    } else {
      const primaryLanguage = systemLanguage.split('-')[0];
      const hasLanguage = languages.find(item => item.type === primaryLanguage);

      if (hasLanguage) {
        i18n.changeLanguage(hasLanguage.type);
        localStorage.setItem('preferLanguage', hasLanguage.type);
      } else {
        i18n.changeLanguage(defaultLanguage);
        localStorage.setItem('preferLanguage', defaultLanguage);
      }
    }
  }, []);

  return (
    <div className={styles.form__wrapper}>
      <div className={styles.logo}>
        <span className={styles.logo__text}>OAZIS</span>
      </div>
      <form
        className={styles.form}
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <InputBar
          name="email"
          value={formik.values.email}
          disabled={false}
          label={t('login')}
          star={false}
          placeholder={t('login_validate')}
          onChange={formik.handleChange}
          error={!!formik.errors.email}
          errors={formik.errors.email}
        />

        <InputPassword
          star={false}
          value={formik.values.password}
          label={t('password')}
          name="password"
          placeholder="***********"
          onChange={formik.handleChange}
          disabled={false}
          popover={false}
          error={!!formik.errors.password}
          errors={formik.errors.password}
          isPasswordVisible={true}
        />

        <div className={styles.form__button}>
          <Button
            text={t('sign_in')}
            type="submit"
            disabled={!(formik.isValid && formik.dirty)}
          />
        </div>
      </form>
    </div>
  );
};

export default AuthForm;
